import React, { useState, useEffect } from 'react';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import CustomSelect from '../home/collections/CustomSelect';
import { validateNamesForField } from '../../common/utils/validate';
import { TYPE_LABEL_MAP, FIELD_TYPE_OPTIONS } from '../../common/utils/prompt-constants';
import FormFieldWithLabel from '../common/FormFieldWithLabel';

export const PromptDataDisplay = ({
  promptFields = [],
  extractedFields = [],
  onUpdateJsonConfig,
  promptName,
  onSubmit,
  actions,
  showCancel = true,
  onCancel,
  isSavedCollection,
}) => {
  const [promptHeaders, setPromptHeaders] = useState([]);
  const [initialPromptHeaders, setInitialPromptHeaders] = useState([]);
  const [isPromptNameValid, setIsPromptNameValid] = useState(true);
  const [localPromptName, setLocalPromptName] = useState(promptName || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [hasBeenSaved, setHasBeenSaved] = useState(isSavedCollection);

  useEffect(() => {
    if (Array.isArray(promptFields) && promptFields.length > 0) {
      const updatedFields = promptFields.map((field) => ({
        ...field,
        title: field.title || (field.fieldTitle && field.fieldTitle.en) || '',
        type: field.type || 'text',
      }));
      setPromptHeaders(updatedFields);
      setInitialPromptHeaders(JSON.parse(JSON.stringify(updatedFields)));
    } else {
      setPromptHeaders([]);
      setInitialPromptHeaders([]);
    }
  }, [promptFields]);

  useEffect(() => {
    const hasChanged = () => {
      if (promptHeaders.length !== initialPromptHeaders.length) return true;
      return promptHeaders.some((field, i) => {
        const initialField = initialPromptHeaders[i];
        return field.title !== initialField.title || field.type !== initialField.type;
      });
    };
    setHasChanges(hasChanged());
  }, [promptHeaders, initialPromptHeaders]);

  const handleFieldTitleChange = (index, value) => {
    setPromptHeaders((prevHeaders) =>
      prevHeaders.map((field, i) =>
        i === index ? { ...field, title: value, fieldTitle: { en: value } } : field,
      ),
    );
  };

  const handleDataTypeChange = (index, nameArray, selectedOption) => {
    if (!selectedOption) return;
    setPromptHeaders((prevHeaders) =>
      prevHeaders.map((field, i) =>
        i === index ? { ...field, type: selectedOption.value } : field,
      ),
    );
  };

  const handleDeleteField = (index) => {
    setPromptHeaders((prevHeaders) => prevHeaders.filter((_, i) => i !== index));
  };

  const handleAddField = () => {
    const newField = { title: '', type: 'text', fieldTitle: { en: '' } };
    setPromptHeaders((prevHeaders) => [...prevHeaders, newField]);
  };

  const validatePromptName = (name) => /^[a-zA-Z\s]+$/.test(name);

  const handleSave = async (values) => {
    if (!localPromptName || !validatePromptName(localPromptName)) {
      setIsPromptNameValid(false);
      return;
    }

    const baseFieldTemplate =
      extractedFields && extractedFields.length > 0
        ? JSON.parse(JSON.stringify(extractedFields[0][0]))
        : {};
    const createDefaultField = (title, type) => {
      const field = JSON.parse(JSON.stringify(baseFieldTemplate));
      field.fieldName = title;
      field.fieldTitle = { en: title };
      field.placeholder = { en: title };
      field.type = type || 'text';
      field.refCollection = {
        collectionField: '',
        collectionName: '',
        displayType: 'dropdown',
        isFileType: false,
        isShowAsRadioOrCheckBox: false,
        isShowAsText: false,
        parentCollectionField: '',
      };

      return field;
    };

    const flatExtractedFields = extractedFields ? extractedFields.flat() : [];
    const updatedFields = promptHeaders.map((field) => {
      const title = field.title;
      const type = field.type;
      const existingField = flatExtractedFields.find((f) => f.fieldTitle.en === title);

      let updatedField;
      if (existingField) {
        updatedField = { ...existingField, type };
      } else {
        updatedField = createDefaultField(title, type);
      }

      if (type !== 'static_option') {
        updatedField.staticOptions = [];
      } else if (!updatedField.staticOptions) {
        updatedField.staticOptions = [];
      }

      return updatedField;
    });

    const updatedJsonConfig = {
      collectionTitle: localPromptName,
      fields: [updatedFields],
    };

    onUpdateJsonConfig(updatedJsonConfig);

    try {
      await onSubmit(updatedJsonConfig);
      setInitialPromptHeaders(JSON.parse(JSON.stringify(promptHeaders)));
      setHasChanges(false);
      setHasBeenSaved(true);
      if (onCancel) onCancel();
    } catch (err) {
      console.error('Error during save:', err);
    }
  };

  const initialValues = promptHeaders.reduce((acc, field, index) => {
    acc[`header_${index}`] = field.title || '';
    acc[`type_${index}`] = field.type || 'text';
    return acc;
  }, {});

  const getTypeLabel = (type) => {
    if (TYPE_LABEL_MAP[type]) return TYPE_LABEL_MAP[type];
    if (typeof type === 'string' && type.length > 0) {
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
    return '';
  };

  const isSubmitDisabled = (submitting) => {
    if (submitting) return true;
    if (!hasBeenSaved) return false;
    if (hasBeenSaved && hasChanges) return false;
    return true;
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-md-12">
          <div className="form-group">
            <FormFieldWithLabel
              cta={false}
              label="Collection Name"
              icon="text"
              isRequired={true}
              infoIcon={true}
              infoDescription="Name for your collection to save your records."
            >
              <input
                type="text"
                name="promptName"
                className="form-control form-control-sm mt-2"
                value={localPromptName}
                placeholder="Prompt Name"
                onChange={(e) => {
                  setLocalPromptName(e.target.value);
                  setIsPromptNameValid(true);
                  setHasChanges(true);
                }}
                disabled={hasBeenSaved}
              />
            </FormFieldWithLabel>
          </div>
          <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSave}>
            {({ values, handleChange, setFieldValue, isSubmitting }) => (
              <Form>
                {promptHeaders.map((field, index) => (
                  <div key={index} className="form-row mb-2">
                    <div className="col-sm-6">
                      <Field
                        type="text"
                        name={`header_${index}`}
                        className="form-control form-control-sm"
                        validate={(value) =>
                          value ? validateNamesForField(value) : 'Field name is required'
                        }
                        value={values[`header_${index}`]}
                        onChange={(e) => {
                          handleChange(e);
                          handleFieldTitleChange(index, e.target.value);
                        }}
                        disabled={field.type == 'reference'}
                      />
                      <ErrorMessage
                        name={`header_${index}`}
                        component="div"
                        className="text-danger"
                      />
                    </div>
                    <div className="col-sm-5">
                      <CustomSelect
                        name={`type_${index}`}
                        isMulti={false}
                        isDisabled={field.type == 'reference'}
                        onChange={(name, selectedOption) => {
                          handleDataTypeChange(index, name, selectedOption);
                          if (selectedOption && selectedOption.value) {
                            setFieldValue(`type_${index}`, selectedOption.value);
                          } else {
                            setFieldValue(`type_${index}`, '');
                          }
                        }}
                        options={FIELD_TYPE_OPTIONS}
                        value={{
                          label: getTypeLabel(field.type),
                          value: field.type,
                        }}
                      />
                    </div>
                    <button
                      className={`btn btn-xs ${
                        field.type == 'reference' ? 'btn-default' : 'btn-theme-delete'
                      } btn-icon-only ml-1`}
                      type="button"
                      onClick={() => handleDeleteField(index)}
                      disabled={field.type == 'reference'}
                    >
                      <i className="wb webconnect-times"></i>
                    </button>
                  </div>
                ))}

                <div className="d-flex justify-content-end mt-3 mr-3">
                  <button
                    type="button"
                    className="btn btn-theme-submit btn-sm"
                    onClick={handleAddField}
                  >
                    <i className="wb webconnect-plus"></i> Add Field
                  </button>
                </div>

                <div className="d-flex justify-content-between mt-3 mr-3">
                  <button
                    type="button"
                    className={`btn btn-outline-secondary btn-sm ${showCancel ? '' : 'invisible'}`}
                    onClick={onCancel}
                  >
                    <i className="wb webconnect-times"></i> Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn btn-sm ${
                      isSubmitDisabled(isSubmitting) ? 'btn-default' : 'btn-theme-submit'
                    }`}
                    disabled={isSubmitDisabled(isSubmitting)}
                  >
                    <i className="wb webconnect-save-regular"></i>&nbsp;
                    {hasBeenSaved ? 'Update Collection' : 'Create Collection'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
